import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useSeo, getSiteUrl } from "@/hooks/useSeo";
import { mangaCategories } from "@/mocks/mangaCategories";
import { mangaList } from "@/mocks/mangaList";
import MangaCard from "@/components/feature/MangaCard";
import Breadcrumb from "@/components/feature/Breadcrumb";
import SectionHeading from "@/components/feature/SectionHeading";

export default function CategoryPage() {
  const { categoryId } = useParams<{ categoryId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const search = searchParams.get("search")?.trim() ?? "";

  const activeId = categoryId && mangaCategories.some((c) => c.id === categoryId) ? categoryId : "all";
  const activeCategory = mangaCategories.find((c) => c.id === activeId);
  const [active, setActive] = useState(activeId);

  useEffect(() => {
    setActive(activeId);
  }, [activeId]);

  const filtered = useMemo(() => {
    const kw = search.toLowerCase();
    return mangaList.filter((manga) => {
      const matchCategory = active === "all" || manga.categories.includes(active);
      const matchSearch =
        !kw ||
        manga.title.toLowerCase().includes(kw) ||
        manga.titleEn.toLowerCase().includes(kw) ||
        manga.author.toLowerCase().includes(kw) ||
        manga.tags.some((tag) => tag.includes(kw));
      return matchCategory && matchSearch;
    });
  }, [active, search]);

  const ranking = useMemo(() => [...mangaList].sort((a, b) => b.rating - a.rating).slice(0, 6), []);

  const pageTitle = active === "all"
    ? "全部漫画-免费在线阅读-土豪漫画网"
    : `${activeCategory?.name}漫画专区-免费在线阅读-土豪漫画网`;

  const pageDescription = active === "all"
    ? "土豪漫画网全部漫画免费在线阅读,涵盖热血冒险、少年漫画、奇幻战斗、校园搞笑等热门分类,高清画质每日更新。"
    : `${activeCategory?.name}漫画免费在线阅读,在土豪漫画网可以找到大量${activeCategory?.desc}作品,高清画质随点随读。`;

  const jsonLd = useMemo(() => {
    const url = getSiteUrl(`/category${active !== "all" ? `/${active}` : ""}`);
    return [
      {
        "@context": "https://storyvault3.github.io",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: getSiteUrl("/") },
          { "@type": "ListItem", position: 2, name: active === "all" ? "全部漫画" : activeCategory?.name, item: url },
        ],
      },
      {
        "@context": "https://storyvault3.github.io",
        "@type": "CollectionPage",
        name: pageTitle,
        url,
        inLanguage: "zh-CN",
        about: activeCategory?.name ?? "全部漫画",
      },
    ];
  }, [active, activeCategory, pageTitle]);

  useSeo({
    title: pageTitle,
    description: pageDescription,
    keywords: `${activeCategory?.name ?? "漫画"},免费阅读,土豪漫画网,少年漫画`,
    path: `/category${active !== "all" ? `/${active}` : ""}`,
    jsonLd,
  });

  const handleTabClick = (id: string) => {
    navigate(id === "all" ? "/category" : `/category/${id}`);
  };

  return (
    <div className="bg-background-100/50">
      <div className="border-b border-background-200 bg-background-50">
        <div className="mx-auto max-w-7xl px-4 py-8 md:px-6">
          <Breadcrumb items={[{ label: "首页", to: "/" }, { label: active === "all" ? "全部漫画" : "漫画分类" }]} />
          <div className="mt-4 flex flex-col gap-2 md:flex-row md:items-end md:justify-between">
            <div>
              <h1 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
                {active === "all" ? "全部漫画" : `${activeCategory?.name}漫画`}
              </h1>
              <p className="mt-2 max-w-2xl text-sm text-foreground-500">
                {active === "all"
                  ? "海量热血少年漫画免费在线阅读,高清画质,每日更新,找到你喜欢的作品开始追更。"
                  : activeCategory?.desc}
              </p>
            </div>
            <p className="shrink-0 text-sm text-foreground-500">
              共 <strong className="text-primary-600">{filtered.length}</strong> 部作品
            </p>
          </div>

          <div className="mt-6 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => handleTabClick("all")}
              className={`whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                active === "all"
                  ? "bg-primary-500 text-background-50"
                  : "bg-background-100 text-foreground-700 hover:bg-background-200"
              }`}
            >
              全部
            </button>
            {mangaCategories.map((category) => (
              <button
                key={category.id}
                type="button"
                onClick={() => handleTabClick(category.id)}
                className={`whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                  active === category.id
                    ? "bg-primary-500 text-background-50"
                    : "bg-background-100 text-foreground-700 hover:bg-background-200"
                }`}
              >
                {category.name}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-4 py-10 md:px-6">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
          <div className="lg:col-span-2">
            {search && (
              <p className="mb-5 text-sm text-foreground-600">
                搜索关键词「<strong className="text-primary-600">{search}</strong>」,共找到 {filtered.length} 部作品
              </p>
            )}
            {filtered.length > 0 ? (
              <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
                {filtered.map((manga) => (
                  <MangaCard key={manga.id} manga={manga} />
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-50 px-6 py-16 text-center">
                <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-100 text-2xl text-foreground-400">
                  <i className="ri-inbox-2-line" aria-hidden="true" />
                </span>
                <h2 className="mt-4 text-base font-bold text-foreground-950">没有找到相关作品</h2>
                <p className="mt-1.5 max-w-xs text-sm text-foreground-500">
                  试试更换搜索关键词,或切换到其他分类浏览。
                </p>
                <button
                  type="button"
                  onClick={() => navigate("/category")}
                  className="mt-5 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-bold text-background-50 transition-colors hover:bg-primary-600"
                >
                  查看全部漫画
                </button>
              </div>
            )}
          </div>

          <aside className="hidden lg:block" aria-label="人气排行">
            <SectionHeading title="人气排行" moreTo="/category" moreLabel="更多" />
            <div className="flex flex-col gap-2.5">
              {ranking.map((manga, index) => (
                <Link
                  key={manga.id}
                  to={`/article/${manga.latestChapterId}`}
                  className="group flex items-center gap-3 rounded-lg border border-background-200 bg-background-50 px-3 py-2.5 transition-all hover:border-primary-300"
                  title={`阅读${manga.title}漫画`}
                >
                  <span className={`w-6 shrink-0 text-center font-heading text-lg font-bold ${index < 3 ? "text-primary-500" : "text-foreground-300"}`}>
                    {index + 1}
                  </span>
                  <div className="relative h-14 w-10 shrink-0 overflow-hidden rounded-md">
                    <img
                      src={manga.cover}
                      alt={`${manga.title}漫画封面`}
                      title={`${manga.title}漫画`}
                      className="h-full w-full object-cover object-top"
                    />
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3 className="truncate text-sm font-bold text-foreground-950 transition-colors group-hover:text-primary-600">
                      {manga.title}
                    </h3>
                    <p className="mt-0.5 truncate text-xs text-foreground-500">{manga.latestChapter}</p>
                  </div>
                  <span className="shrink-0 font-heading text-base font-bold text-primary-600">{manga.rating}</span>
                </Link>
              ))}
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}